// Shared helpers for per-methodology completeness/readiness, used by the Completeness
// panel and the Upload panel's "Estado general" summary. Consumes the `methodologies[]`
// array the /completeness endpoint returns per article: [{ methodology, completeness,
// readinessStatus }].

export const READY_GREEN = "#059669";
export const ASSUM_ORANGE = "#d97706";
export const BLOCKED_RED = "#dc2626";
// Business-rule violations (INVALID_VALUE): data is present but logically wrong → "Revisar".
export const REVIEW_PURPLE = "#9333ea";

// Preferred display order; only methodologies present in the data are shown (i.e. the
// account's active ones — the backend only computes a methodology when it has fields in scope).
export const METH_ORDER = ["LCA", "CEI", "EcoScore", "AGEC"];

// Colour of a methodology cell/card by its readiness (green = ready to calculate).
export const methColor = (rs) =>
  rs === "BLOCKED"
    ? BLOCKED_RED
    : rs === "INCOMPLETE"
    ? ASSUM_ORANGE
    : rs === "WITH_ASSUMPTIONS"
    ? ASSUM_ORANGE
    : READY_GREEN;

// Gap status → user-facing bucket (faltan / revisar / asumibles / asumidos / aportados).
// `revisar` = business-rule violations (INVALID_VALUE): the datum is present but logically invalid,
// so the action is "correct a value", not "provide missing data". It is its own bucket, but still
// counts as "not ready" (see pendingOf).
export const BUCKET_OF = {
  MISSING: "faltan",
  BLOCKING: "faltan",
  INVALID_VALUE: "revisar",
  ASSUMPTION_REJECTED: "faltan",
  REQUESTED: "faltan",
  EXPIRED: "faltan",
  UNRESOLVED_MAPPING: "faltan",
  ASSUMPTION_PENDING: "asumibles",
  RESOLVED_ASSUMPTION: "asumidos",
  RESOLVED_PROVIDED: "aportados",
};

// Sum a field's statusCounts into the buckets.
export const bucketTotalsOf = (statusCounts) => {
  const t = { faltan: 0, revisar: 0, asumibles: 0, asumidos: 0, aportados: 0 };
  Object.entries(statusCounts || {}).forEach(([st, n]) => {
    t[BUCKET_OF[st] || "faltan"] += n;
  });
  return t;
};

// "Por resolver" = needs an action to become ready: absent data (faltan) + invalid data (revisar).
export const pendingOf = (statusCounts) => {
  const b = bucketTotalsOf(statusCounts);
  return b.faltan + b.revisar;
};

// Why a datum is flagged INVALID_VALUE ("Revisar"), by fieldKey. Each business rule
// (BusinessRulesService, backend) targets a unique fieldKey, so the reason is derivable from it.
// Only meaningful when the gap status is INVALID_VALUE (the same field can also be plainly MISSING).
// NOTE: mirrors the rules in businessRules.service.ts — keep in sync if a rule changes.
export const INVALID_REASON = {
  "article.name": "Nombre de artículo duplicado dentro del proyecto.",
  "articlematerial.material.name": "El material vinculado pertenece a otro proyecto.",
  "article.weight": "El peso del artículo se desvía más de un 10% de la suma de los pesos de sus materiales.",
  "articlematerial.amount": "Cantidad de material inválida: material declarado sin peso, o peso declarado sin material.",
  "material.name": "Material duplicado dentro del mismo artículo.",
  "material.process_applicability": "Proceso declarado (tintura/estampado/tratamiento o procesado) imposible para el grupo de material.",
};
export const invalidReasonFor = (fieldKey) =>
  INVALID_REASON[fieldKey] || "Valor inválido según las reglas de negocio.";

// Fields with MISSING (faltan) gaps that are required (M/M-A) for the given methodology —
// i.e. "qué falta para poder calcular esta metodología".
export const missingFieldsFor = (gapFields, methodology) =>
  (gapFields || [])
    .map((f) => ({ f, faltan: bucketTotalsOf(f.statusCounts).faltan }))
    .filter((x) => x.faltan > 0 && criticalFor(x.f).includes(methodology))
    .sort((a, b) => b.faltan - a.faltan);

// Methodologies for which a field is required (M or M-A) — i.e. "crítico para".
// Reads the requiredFor* profile the /gaps?groupBy=field response carries per field.
const METH_REQ_KEYS = [
  ["LCA", "requiredForLCA"],
  ["CEI", "requiredForCEI"],
  ["EcoScore", "requiredForEcoScore"],
  ["AGEC", "requiredForAGEC"],
];
export const criticalFor = (field) =>
  METH_REQ_KEYS.filter(([, k]) => field[k] === "M" || field[k] === "M-A").map(([m]) => m);

// Methodology display name → its feature `key` in /accountFeatures.
const METH_FEATURE_KEY = { LCA: "lca", CEI: "cei", EcoScore: "eco_score", AGEC: "agec" };

// From the /accountFeatures response items ({ feature: { key } }), the contracted
// methodologies (display names), in preferred order.
export const contractedMethodologies = (features) => {
  const keys = new Set((features || []).map((af) => (af.feature?.key || af.key || "").toLowerCase()));
  return METH_ORDER.filter((m) => keys.has(METH_FEATURE_KEY[m]));
};

// The active methodologies present across the loaded articles, in preferred order.
// When `allowed` (the contracted methodologies) is provided, only those are kept.
export const activeMethodologies = (articles, allowed) => {
  const present = new Set();
  (articles || []).forEach((a) => (a.methodologies || []).forEach((m) => present.add(m.methodology)));
  const ordered = METH_ORDER.filter((m) => present.has(m));
  const extra = [...present].filter((m) => !METH_ORDER.includes(m));
  let list = ordered.concat(extra);
  if (allowed && allowed.length) list = list.filter((m) => allowed.includes(m));
  return list;
};

// Project-level rollup for one methodology: avg completeness + counts by readiness.
export const methodologyStats = (articles, m) => {
  let sum = 0,
    n = 0,
    blocked = 0,
    incomplete = 0,
    withAssum = 0;
  (articles || []).forEach((a) => {
    const e = (a.methodologies || []).find((x) => x.methodology === m);
    if (!e || e.completeness == null) return;
    sum += e.completeness;
    n += 1;
    if (e.readinessStatus === "BLOCKED") blocked += 1;
    else if (e.readinessStatus === "INCOMPLETE") incomplete += 1;
    else if (e.readinessStatus === "WITH_ASSUMPTIONS") withAssum += 1;
  });
  return { avg: n ? Math.round(sum / n) : null, blocked, incomplete, withAssum, count: n };
};

// Rollup card descriptor for a methodology: "X/Y ready to calculate" + state + colour.
// "Ready to calculate" = READY or WITH_ASSUMPTIONS (INCOMPLETE/BLOCKED are not ready).
export const methodologyCard = (articles, m) => {
  const s = methodologyStats(articles, m);
  const ready = s.count - s.blocked - s.incomplete;
  const total = s.count;
  const blocked = s.blocked > 0;
  const color = blocked ? BLOCKED_RED : s.incomplete > 0 ? ASSUM_ORANGE : READY_GREEN;
  const sub = blocked
    ? `${s.blocked} bloqueado${s.blocked === 1 ? "" : "s"}`
    : s.incomplete > 0
    ? `${s.incomplete} incompleto${s.incomplete === 1 ? "" : "s"}`
    : "Lista para calcular";
  const readyLabel = total ? `${ready}/${total}` : "—";
  return { methodology: m, avg: s.avg, ready, total, readyLabel, blocked, color, sub, stats: s };
};
